"use client";

import { useSession } from "next-auth/react";
import { useRouter } from "@/i18n/routing";
import { useSearchParams } from "next/navigation";
import { stripLocaleFromPath } from "@/lib/i18n-utils";
import { useEffect } from "react";

interface AuthPageWrapperProps {
  children: React.ReactNode;
  className?: string;
}

export function AuthPageWrapper({ children, className }: AuthPageWrapperProps) {
  const { data: session, status } = useSession();
  const router = useRouter();
  const searchParams = useSearchParams();

  // Get the callback URL or default to dashboard
  const rawCallbackUrl = searchParams?.get("callbackUrl") || "/dashboard";
  // Strip locale prefix since i18n router will add it automatically
  const callbackUrl = stripLocaleFromPath(rawCallbackUrl);

  // Redirect already authenticated users
  useEffect(() => {
    if (status === "authenticated" && session) {
      router.push(callbackUrl);
    }
  }, [status, session, router, callbackUrl]);

  // Show loading while checking session
  if (status === "loading") {
    return (
      <div
        className={`flex min-h-screen items-center justify-center ${className || ""}`}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Don't render the children if user is already authenticated
  if (status === "authenticated") {
    return null;
  }

  return <>{children}</>;
}
